/**
 * useOrderReviewLines — the Summary "Your order" box's data.
 *
 * Built on the SAME `buildCartDisplay` the cart sidebar uses, so the review
 * column and the sidebar cannot disagree about what is in the cart — the failure
 * mode legacy had, where its review listing read a different source (the legacy
 * `selectedProducts` map) and showed nothing at all on a v2 order.
 *
 * What it adds on top of a display row is what the NOTICES need: the chosen
 * variant's wire `kind`, the kinds of the add-ons that line actually selected,
 * and the client's own note.
 */

import { useMemo } from 'react'

import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'
import { useGetVirtualStagingFurnitureStyles } from 'dataQueries/virtualStaging.query'

import type { LandRegistrationSheetDTO } from 'models/purchaseFlow/AuthoritiesDocumentDTO'
import { FloorPlanRenderType } from 'constants/floorPlan'

import { usePfStore, type PfStoreState } from '../../store'
import { makeConfigKey } from '../../store/slices/productConfigSlice'
import { documentMarkersOf } from '../../fileHub/useSessionDocumentReconcile'
import type { PropertyInfoDraftDTO } from '../../modules/propertyInfo/propertyInfo.dto'
import type { FloorPlanConfigDraftDTO } from '../../modules/floorPlanConfig/floorPlanConfig.dto'
import { findVariant } from '../../store/serializeLine'
import { buildCartDisplay, type CartExtraRow } from '../../components/cart/useCartDisplay'
import { groupRowsByPack } from '../../components/cart/packGroups'
import type { OrderReviewLine, ReviewFloorPlan, ReviewStagingRoom, ReviewUpload } from './OrderReviewSection.component'
import { usePfV2VisualUpload } from '../../fileHub/kinds/stagingVisuals'
import { useOrderExtras } from '../../store/stagingRenovation'
import { normalizeRoom, type StagingDraftDTO } from '../../modules/staging/staging.dto'
import { mapGet } from '../../modules/staging/stagingRoom.logic'
import { shootOutputLines } from '../../store/shootOutput'
import { StagingType } from 'models/virtualStaging'
import { VirtualFurnitureRoomRemovableOption, VirtualFurnitureRoomType } from 'constants/virtualFurniture'

/** What the review box needs: the product lines AND the order-level charges. */
export interface OrderReview {
  lines: OrderReviewLine[]
  /** Inside the total, so they belong on the box too. */
  extras: CartExtraRow[]
  /** Currency of the cart, for formatting the extras' amounts. */
  currency: string | null
}

/** Every file a line carries (its own uploads and the documents its pages recorded), by the client's name,
 *  with a preview when the file is a visual this session knows. Files a staging room already shows are left out. */
const uploadsOf = (
  cartItemId: string,
  attachments: PfStoreState['attachments'],
  productConfigs: PfStoreState['productConfigs'],
  displayNames: Record<string, string>,
  urlByFile: Record<string, string>,
  stagingRoomKeys: ReadonlySet<string>
): ReviewUpload[] => {
  const ownConfigs = Object.fromEntries(Object.entries(productConfigs).filter(([key]) => key.startsWith(`${cartItemId}|`)))
  const stored = [
    ...(attachments[cartItemId] ?? []).map((file) => file.gcFilename),
    ...documentMarkersOf(ownConfigs).flatMap((markers) => markers.filenames),
  ]
  return [...new Set(stored)]
    .filter((file) => !stagingRoomKeys.has(file))
    .map((file) => ({ name: displayNames[file] ?? file, url: urlByFile[file] }))
}

/** The staging rooms of a line, in the order the client created them. */
const stagingRoomsOf = (
  config: unknown,
  urlByFile: Record<string, string>,
  styleOfCode: Record<string, string | undefined>
): ReviewStagingRoom[] => {
  const rooms = (config as StagingDraftDTO | undefined)?.rooms ?? {}
  return Object.entries(rooms)
    .map(([key, raw]) => normalizeRoom(key, raw))
    .sort((a, b) => a.roomCreationSortingOrder - b.roomCreationSortingOrder)
    .map((room) => {
      const removal = mapGet(room.renovations, VirtualFurnitureRoomType.WHOLE_ROOM) ?? room.furnitureRemoval ?? undefined
      return {
        key: room.key,
        url: urlByFile[room.key],
        backboneDecides: room.type === StagingType.BKBN,
        roomTypes: room.roomTypes,
        // The furniture the client picked names its style; `styleCategory` is only set by "apply to all rooms".
        style: room.styleCategory ?? room.styleCodeMap.map(([, code]) => styleOfCode[code]).find(Boolean),
        removal: removal && removal !== VirtualFurnitureRoomRemovableOption.NONE ? removal : undefined,
        decorations: room.decorations.length,
        wall: !!mapGet(room.renovations, VirtualFurnitureRoomType.WALL_MATERIALS),
        floor: !!mapGet(room.renovations, VirtualFurnitureRoomType.FLOORING),
      }
    })
}

/** Typed sheets only: a lookup left to Backbone sends none, and the add-on line already says so. */
const landRegisterSheetsOf = (config: unknown): LandRegistrationSheetDTO[] | undefined => {
  const dto = config as PropertyInfoDraftDTO | undefined
  if (!dto || dto.searchLandRegistrationDetails) return undefined
  return dto.landRegistrationSheets?.filter((sheet) => sheet.name || sheet.district)
}

/** What the client chose for a floor plan, once a render type has been picked. */
const floorPlanOf = (config: unknown): ReviewFloorPlan | undefined => {
  const dto = config as FloorPlanConfigDraftDTO | undefined
  if (!dto?.currentFloorPlanRenderType) return undefined
  return {
    is3D: dto.currentFloorPlanRenderType === FloorPlanRenderType.RENDER_3D,
    style: dto.style || undefined,
    propertyType: dto.selectedPropertyType,
    comment: dto.clientComment?.trim() || undefined,
  }
}

export const useOrderReviewLines = (): OrderReview => {

  const items = usePfStore((s) => s.items)
  const attachments = usePfStore((s) => s.attachments)
  const productConfigs = usePfStore((s) => s.productConfigs)
  const uploadDisplayNames = usePfStore((s) => s.uploadDisplayNames)
  const { visuals } = usePfV2VisualUpload()
  const hasStaging = Object.keys(productConfigs).some((key) => key.endsWith('|staging'))
  const furnitureStyles = useGetVirtualStagingFurnitureStyles(hasStaging).data

  // Same derivation as `useCatalogue`'s other callers, or React Query serves a
  // second cache entry (and a second GET).
  const catalogue = usePfCatalogue().data
  const extras = useOrderExtras(catalogue)

  return useMemo(() => {
    const { rows, extraRows, currency } = buildCartDisplay(items, catalogue, extras)

    const packName = (packProductCode: string) =>
      catalogue?.bundles.find((bundle) => bundle.variants.some((variant) => variant.id === packProductCode))?.displayName ?? packProductCode

    // A pack's lines are listed together, where its first line sits, as the cart sidebar lists them.
    const ordered = groupRowsByPack(rows, items).flatMap((entry) => entry.kind === 'line' ? [entry.row] : entry.rows)

    const styleOfCode: Record<string, string | undefined> = {}
    for (const style of furnitureStyles ?? []) styleOfCode[style.code] = style.style
    const urlByFile: Record<string, string> = {}
    for (const visual of visuals) if (visual.webUrl) urlByFile[visual.id] = visual.webUrl

    const fromShootById = shootOutputLines(items, catalogue?.shellProducts)

    const lines = ordered.map((row) => {
      const fromShoot = fromShootById.get(row.id)
      if (fromShoot) {
        const item = items.find((candidate) => candidate.id === row.id)
        const variant = item ? findVariant(catalogue, item.variantId) : undefined
        return { ...row, kind: variant?.kind, productCode: variant?.productCode, selectedOptionKinds: [], note: item?.note?.trim() || undefined, fromShoot }
      }
      const item = items.find((candidate) => candidate.id === row.id)
      const variant = item ? findVariant(catalogue, item.variantId) : undefined

      // Only the add-ons this line actually selected: an offered-but-unticked 3D
      // option must not suppress the floor-plan disclaimer.
      const selectedOptionKinds = (variant?.options ?? [])
        .filter((option) => {
          const value = item?.optionValues?.[option.id]
          return value !== undefined && value !== false && value !== 0 && value !== ''
        })
        .map((option) => option.productKind)
        .filter((kind): kind is string => !!kind)

      const stagingRooms = stagingRoomsOf(productConfigs[makeConfigKey(row.id, 'staging')], urlByFile, styleOfCode)

      return {
        ...row,
        kind: variant?.kind,
        productCode: variant?.productCode,
        selectedOptionKinds,
        note: item?.note?.trim() || undefined,
        aiEditing: item?.aiEditing,
        uploads: uploadsOf(row.id, attachments, productConfigs, uploadDisplayNames, urlByFile, new Set(stagingRooms.map((room) => room.key))),
        stagingRooms,
        landRegisterSheets: landRegisterSheetsOf(productConfigs[makeConfigKey(row.id, 'property_info')]),
        floorPlan: floorPlanOf(productConfigs[makeConfigKey(row.id, 'floor_plan_config')]),
        pack: item?.pack ? { selectionId: item.pack.selectionId, name: packName(item.pack.productCode) } : undefined,
      }
    })

    return { lines, extras: extraRows, currency }
  }, [catalogue, extras, items, attachments, productConfigs, uploadDisplayNames, visuals, furnitureStyles])
}
